import { Empty } from "antd";
import { type FieldProps } from "formik";
import { BTFileSystem, BTServiceFileUpload } from "legacyComponents/FileUploadContainer.types";
import moment from "moment";
import { Component } from "react";
import { type RouteComponentProps, withRouter } from "react-router";

import { BTCheckbox, BTInput, BTNumberInput, BTTextArea } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { BdsFormItem } from "@buildertrend/components";

import { BTSelectItem, type IDropDownItem } from "types/apiResponse/apiResponse";
import { CustomFieldDataType } from "types/enum";

import { track } from "utilities/analytics/analytics";
import { ConvertToTempFileType } from "utilities/document/document";
import yup from "utilities/form/yup";
import { lazyLoadWithRetry } from "utilities/react.utilities";

import BdsSectionBox from "commonComponents/bdsWrappers/BdsSectionBox/BdsSectionBox";
import { BTDatePicker } from "commonComponents/btWrappers/BTDatePicker/BTDatePicker";
import { BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTInputLink } from "commonComponents/btWrappers/BTInputLink/BTInputLink";
import { BTInputNumber } from "commonComponents/btWrappers/BTInputNumber/BTInputNumber";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTSelectEditable } from "commonComponents/btWrappers/BTSelectEditable/BTSelectEditable";
import { BTSelectUser } from "commonComponents/btWrappers/BTSelectUser/BTSelectUser";
import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";
import { BTUpload } from "commonComponents/btWrappers/BTUpload/BTUpload";
import {
    type CustomFieldDetailsEntity,
    type DropdownCustomFieldMapping,
} from "commonComponents/entity/customField/CustomFieldDetails/CustomFieldDetails.api.types";
import { ErrorLoading } from "commonComponents/error/ErrorLoading";
import { Currency } from "commonComponents/financial/Currency/Currency";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";
import { RequiredFieldIndicator } from "commonComponents/utilities/RequiredFieldIndicator/RequiredFieldIndicator";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import "./CustomFieldContainer.less";

import {
    type ICustomFieldResponse,
    type IHasCustomFieldContainer,
    type ISelectedFile,
} from "./CustomFieldContainer.types";

const CustomFieldDetails = lazyLoadWithRetry(
    () => import("commonComponents/entity/customField/CustomFieldDetails/CustomFieldDetails"),
);

interface ICustomFieldContainerProps {
    fields: IHasCustomFieldContainer;

    /** show field inputs as disabled, but still in form elements
     * @default: false */
    disabled?: boolean;

    /** should the collapse appear expanded by default? */
    expanded: boolean;

    /** disable custom field info section fields?
     * @default: false */
    disableEdit?: boolean;

    /** when using the BDS section box, render it flush without the box border and padding
     * @default: false */
    borderless?: boolean;

    readOnly?: boolean;
}

export interface ICustomFieldFormValues {
    id: number;
    value:
        | Date
        | string
        | number
        | boolean
        | BTServiceFileUpload
        | string[]
        | number[]
        | moment.Moment
        | null;
    dropdownItems?: BTSelectItem[];
    onOwnerPortal: boolean;
    /** The field definition's default value, sent by the API to support detecting modifications on saved drafts. */
    defaultValue?: string;
}

export interface ICustomFieldContainerFormValues {
    customFields: ICustomFieldFormValues[];
}

type ICustomFieldContainerInternalProps = RouteComponentProps &
    FieldProps<IHasCustomFieldContainer> &
    ICustomFieldContainerProps;

class CustomFieldContainerInternalBase extends Component<ICustomFieldContainerInternalProps> {
    static defaultProps = {
        disabled: false,
    };

    private handleDropdownCollectionChange = (
        valueName: string,
        customFieldIndex: number,
        navigationAction: () => void,
    ) => {
        return (
            updatedCustomFields: CustomFieldDetailsEntity,
            dropdownMappings?: DropdownCustomFieldMapping[],
        ) => {
            this.updateSelectedDropdownValue(
                updatedCustomFields,
                customFieldIndex,
                valueName,
                dropdownMappings,
            );
            this.updateDropdownItems(updatedCustomFields, customFieldIndex);
            navigationAction();
        };
    };

    private updateSelectedDropdownValue(
        updatedCustomFields: CustomFieldDetailsEntity,
        customFieldIndex: number,
        valueName: string,
        dropdownMappings?: DropdownCustomFieldMapping[],
    ) {
        const { setFieldValue, values } = this.props.form;
        let selectedValue = values.customFields[customFieldIndex].value;

        if (!Array.isArray(selectedValue)) {
            const isValidSelection = updatedCustomFields.dropDownListValues.some(
                (lv) => lv.value === selectedValue,
            );
            if (!isValidSelection) {
                setFieldValue(valueName, -1);
            }
        } else {
            const newDropdownItems = updatedCustomFields.dropDownListValues;
            selectedValue = selectedValue.map((val) => {
                if (newDropdownItems.some((newItems) => newItems.value === val)) {
                    return val;
                }
                return dropdownMappings?.find((mapping) => val === mapping.oldId)?.newId ?? val;
            });
            selectedValue = selectedValue.filter((val) =>
                newDropdownItems.some((newItems) => newItems.value === val),
            );
            setFieldValue(valueName, selectedValue);
        }
    }

    private updateDropdownItems(
        updatedCustomFields: CustomFieldDetailsEntity,
        customFieldIndex: number,
    ) {
        const { setFieldValue, values } = this.props.form;
        const dropdownItemsFormName = `customFields.${customFieldIndex}.dropdownItems`;

        const updatedDropdownValues = updatedCustomFields.dropDownListValues;
        const hasDefaultValue = updatedCustomFields.dropDownListValues.some(
            (lv) => lv.value === -1,
        );
        if (!hasDefaultValue) {
            const { dropdownItems } = values.customFields[
                customFieldIndex
            ] as ICustomFieldFormValues;
            const defaultItem = dropdownItems && dropdownItems.find((di) => di.value === -1);
            if (defaultItem) {
                updatedDropdownValues.unshift(defaultItem);
            }
        }
        setFieldValue(dropdownItemsFormName, updatedDropdownValues);
    }

    private noOpFunction = () => {};

    private renderCustomField = (
        cf: ICustomFieldResponse,
        cfv: ICustomFieldFormValues,
        label: JSX.Element,
        tooltip: JSX.Element | undefined,
        i: number,
    ) => {
        const {
            form: { handleChange, setFieldValue, setFieldTouched, handleBlur, values },
            disabled,
            disableEdit,
            fields,
        } = this.props;
        const customFieldFormValue = values.customFields.find(
            (value: ICustomFieldResponse) => value.id === cfv.id,
        )!;
        const valueName = `customFields.${i}.value`;
        const testid = `customFields.${cf.id}`;
        const multiSelectTypes = [
            CustomFieldDataType.DropDownCheckList,
            CustomFieldDataType.MultiSelectSubs,
            CustomFieldDataType.MultiSelectUsers,
        ];
        const isMultiSelect = multiSelectTypes.includes(cf.typeId);
        const treeValues = cfv.dropdownItems;
        const selectedValues = customFieldFormValue.value as number[];

        // Field-spefic disabled value overrides the Container disabled value
        const isFieldDisabled = typeof cf.disabled === "boolean" ? cf.disabled : disabled;

        switch (cf.typeId) {
            case CustomFieldDataType.ListOfSubs:
            case CustomFieldDataType.ListOfUsers:
            case CustomFieldDataType.MultiSelectSubs:
            case CustomFieldDataType.MultiSelectUsers:
                return (
                    <BTSelectUser
                        id={valueName}
                        data-testid={testid}
                        value={selectedValues}
                        treeData={treeValues}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        multiple={isMultiSelect}
                        disabled={isFieldDisabled}
                        readOnly={this.props.readOnly}
                        useBds={fields.useBds}
                    />
                );

            case CustomFieldDataType.DropDownList:
            case CustomFieldDataType.DropDownCheckList:
                return (
                    <BTSelectEditable
                        id={valueName}
                        data-testid={testid}
                        value={selectedValues}
                        treeData={treeValues}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        // The add, edit, delete action from the details page handles the collection update
                        onTreeDataChange={this.noOpFunction}
                        multiple={isMultiSelect}
                        disabled={isFieldDisabled}
                        readOnly={this.props.readOnly}
                        isConfigurable={this.props.fields.customFieldsCanConfigure}
                        detailPath={`/CustomField/${cf.id}`}
                        renderDetail={(detailProps) => {
                            return (
                                <BTSuspense>
                                    <CustomFieldDetails
                                        customFieldId={cf.id}
                                        associatedType={this.props.fields.customFieldAssociatedType}
                                        onClose={detailProps.navigateBack}
                                        onAdd={this.handleDropdownCollectionChange(
                                            valueName,
                                            i,
                                            detailProps.navigateBack,
                                        )}
                                        onEdit={this.handleDropdownCollectionChange(
                                            valueName,
                                            i,
                                            detailProps.navigateBack,
                                        )}
                                        onDelete={this.handleDropdownCollectionChange(
                                            valueName,
                                            i,
                                            detailProps.navigateBackForDelete,
                                        )}
                                        disableEdit={disableEdit}
                                        {...detailProps.routeProps}
                                    />
                                </BTSuspense>
                            );
                        }}
                        useBds={fields.useBds}
                    />
                );

            case CustomFieldDataType.Date:
                const date = customFieldFormValue.value as moment.Moment | null;

                return (
                    <BTDatePicker<undefined>
                        id={valueName}
                        data-testid={testid}
                        value={date === null ? undefined : date}
                        onChange={setFieldValue}
                        disabled={isFieldDisabled}
                        readOnly={this.props.readOnly}
                    />
                );

            case CustomFieldDataType.File:
                const selectedFile = customFieldFormValue.value as BTServiceFileUpload;
                const tempFileType = ConvertToTempFileType(
                    this.props.fields.customFieldAssociatedType,
                );

                return (
                    <BTUpload
                        id={valueName}
                        data-testid={testid}
                        setFieldValue={setFieldValue}
                        disabled={isFieldDisabled}
                        value={selectedFile}
                        allowedFileTypes={cf.fileTypes!}
                        tempFileType={tempFileType}
                        readOnly={this.props.readOnly}
                    />
                );

            case CustomFieldDataType.MultiLineText: {
                return (
                    <BTTextArea<undefined>
                        id={valueName}
                        name={valueName}
                        data-testid={testid}
                        value={customFieldFormValue.value as string}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        disabled={isFieldDisabled}
                        readOnly={this.props.readOnly}
                    />
                );
            }
            case CustomFieldDataType.CheckBox:
                return (
                    <BTCheckbox
                        id={valueName}
                        data-testid={valueName}
                        checked={customFieldFormValue.value as boolean}
                        onChange={handleChange}
                        disabled={isFieldDisabled}
                        readOnly={this.props.readOnly}
                    >
                        {label} {tooltip}
                    </BTCheckbox>
                );

            case CustomFieldDataType.Currency:
                let valueAsNumber: number | undefined;
                if (customFieldFormValue.value !== "") {
                    valueAsNumber = Number(customFieldFormValue.value as string);
                }

                return (
                    <Currency
                        id={valueName}
                        data-testid={testid}
                        value={valueAsNumber}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        disabled={isFieldDisabled}
                        readOnly={this.props.readOnly}
                    />
                );

            case CustomFieldDataType.WholeNumber:
                let valueAsWholeNumber: number | undefined = parseInt(
                    `${customFieldFormValue.value}`,
                );
                if (isNaN(valueAsWholeNumber)) {
                    valueAsWholeNumber = undefined;
                }
                const InputComponent = fields.useBds ? BTNumberInput : BTInputNumber;

                return (
                    <InputComponent<undefined>
                        id={valueName}
                        data-testid={testid}
                        value={valueAsWholeNumber}
                        step={1}
                        min={-999999999999}
                        max={999999999999}
                        onChange={setFieldValue}
                        onBlur={handleBlur}
                        disabled={isFieldDisabled}
                        roundToDecimalPlaces={0}
                        allowNull={true}
                        readOnly={this.props.readOnly}
                    />
                );

            case CustomFieldDataType.Link:
                return (
                    <BTInputLink<undefined>
                        id={valueName}
                        data-testid={testid}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        disabled={isFieldDisabled}
                        value={customFieldFormValue.value as string}
                        readOnly={this.props.readOnly}
                        useBds={fields.useBds}
                    />
                );
            case CustomFieldDataType.Text: {
                return (
                    <BTInput<undefined>
                        id={valueName}
                        data-testid={testid}
                        value={customFieldFormValue.value as string}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        disabled={isFieldDisabled}
                        readOnly={this.props.readOnly}
                    />
                );
            }
            default:
                return <div>Unsupported Custom Field</div>;
        }
    };

    private customFieldInputs = () => {
        const {
            fields,
            form: { errors, touched },
        } = this.props;

        if (fields.customFields.length === 0) {
            return <Empty description="No custom fields" />;
        }

        // Iterate over form values (ICustomFieldFormValues) while referencing API metadata (ICustomFieldResponse)
        const formValuesCustomFields: ICustomFieldFormValues[] = this.props.form.values
            .customFields as ICustomFieldFormValues[];
        const customFields = formValuesCustomFields.map(
            (cfv: ICustomFieldFormValues, i: number) => {
                const apiData = fields.customFields.find((cf) => cf.id === cfv.id);
                if (!apiData) {
                    return null;
                }
                const hasError =
                    errors &&
                    errors.customFields &&
                    errors.customFields[i] &&
                    touched &&
                    touched.customFields &&
                    touched.customFields[i];
                const helpMessage = hasError ? errors.customFields![i]!.value : null;
                const isRequired =
                    apiData.validators?.find((x) => x.type === "required") !== undefined;
                const isCheckbox = apiData.typeId === CustomFieldDataType.CheckBox;

                const label = (
                    <span className="custom-field-label">
                        <TextTruncate>{apiData.title}</TextTruncate>
                        {isRequired ? <RequiredFieldIndicator /> : null}
                    </span>
                );

                let tooltip: JSX.Element | undefined;
                if (apiData.toolTipText !== "") {
                    tooltip = (
                        <BTPopover
                            content={apiData.toolTipText}
                            placement="right"
                            removeSpacing={isCheckbox}
                        />
                    );
                }

                if (isCheckbox) {
                    return (
                        <div
                            style={this.props.readOnly ? { pointerEvents: "none" } : undefined}
                            key={`${cfv.id}`}
                            className="ant-row ant-form-item ant-form-item-with-help"
                        >
                            {this.renderCustomField(apiData, cfv, label, tooltip, i)}
                        </div>
                    );
                } else {
                    if (fields.useBds) {
                        return (
                            <BdsFormItem
                                label={
                                    <>
                                        {label}
                                        {tooltip}
                                    </>
                                }
                                key={i}
                                validateStatus={hasError ? "error" : ""}
                                help={helpMessage}
                            >
                                {this.renderCustomField(apiData, cfv, label, tooltip, i)}
                            </BdsFormItem>
                        );
                    }
                    return (
                        <BTFormItem
                            label={
                                <>
                                    {label}
                                    {tooltip}
                                </>
                            }
                            key={i}
                            validateStatus={hasError ? "error" : "success"}
                            help={helpMessage}
                        >
                            {this.renderCustomField(apiData, cfv, label, tooltip, i)}
                        </BTFormItem>
                    );
                }
            },
        );

        return (
            <div
                style={this.props.fields.useBds ? undefined : { maxWidth: 400 }}
                className={
                    this.props.fields.useBds ? "bds-col bds-gap-4 CustomFieldContainerFields" : ""
                }
            >
                {customFields}
            </div>
        );
    };

    render() {
        const { fields, expanded } = this.props;

        if (fields.customFields.length === 0) {
            // we hide the entire section when no items exist
            return null;
        }

        const SectionComponent = fields.useBds ? BdsSectionBox : PageSection;

        return (
            <SectionComponent
                title={
                    this.props.readOnly
                        ? undefined
                        : `Custom fields (${fields.customFields.length})`
                }
                isExpanded={expanded}
                isCollapsible={!this.props.readOnly}
                className={cn("CustomFieldContainer", fields.useBds && "bds-grow")}
                borderless={fields.useBds ? this.props.borderless : undefined}
            >
                {this.customFieldInputs()}
            </SectionComponent>
        );
    }
}

const CustomFieldContainerInternal = track({ component: "Custom Field Container" })(
    CustomFieldContainerInternalBase,
);

class CustomFieldContainerSafe extends Component<
    RouteComponentProps & FieldProps<IHasCustomFieldContainer> & ICustomFieldContainerProps
> {
    render() {
        return (
            <ErrorLoading entityName="custom fields">
                <CustomFieldContainerInternal {...this.props} />
            </ErrorLoading>
        );
    }
}

export const CustomFieldContainer = withRouter(CustomFieldContainerSafe);

function getValueValidator(
    value: ICustomFieldFormValues,
): yup.Schema<number | number[] | Date | boolean | string> {
    const apiData: ICustomFieldResponse = value as ICustomFieldResponse;
    let validator: yup.Schema<number | number[] | Date | boolean | string>;

    switch (apiData.typeId) {
        case CustomFieldDataType.Currency:
        case CustomFieldDataType.WholeNumber:
            validator = yup.string().nullable().typeError("${path} must be a number");
            break;

        case CustomFieldDataType.DropDownList:
        case CustomFieldDataType.ListOfSubs:
        case CustomFieldDataType.ListOfUsers:
            validator = yup.number().nullable();
            break;

        case CustomFieldDataType.DropDownCheckList:
        case CustomFieldDataType.MultiSelectSubs:
        case CustomFieldDataType.MultiSelectUsers:
            validator = yup.array<number>().nullable();
            break;

        case CustomFieldDataType.Date:
            validator = yup.mixed().validMomentDate("date").nullable();
            break;

        case CustomFieldDataType.CheckBox:
            validator = yup.boolean();
            break;

        default:
            validator = yup.string();
    }

    apiData.validators.forEach((value) => {
        if (value.type === "required") {
            switch (apiData.typeId) {
                case CustomFieldDataType.DropDownList:
                case CustomFieldDataType.ListOfUsers:
                case CustomFieldDataType.ListOfSubs:
                    validator = (validator as yup.NumberSchema).requiredDropdown(-1);
                    break;
                case CustomFieldDataType.MultiSelectSubs:
                case CustomFieldDataType.MultiSelectUsers:
                case CustomFieldDataType.DropDownCheckList:
                    validator = (validator as yup.ArraySchema<number>).requiredDropdown();
                    break;
                default:
                    validator = validator.required();
                    break;
            }
        } else if (value.type === "maxLength") {
            validator = (validator as yup.ArraySchema<any> | yup.StringSchema).max(value.value);
        }
    });

    return validator.label(apiData.title);
}

/**
 * Maps a ISelectedFile or BTFile to a AttachFileRequest
 * @param hasChanged set to true when modified so the API updates the attachment
 */
function mapToBTServiceFileUpload(
    file: ISelectedFile | BTFileSystem,
    hasChanged: boolean,
): BTServiceFileUpload {
    if (file instanceof BTFileSystem) {
        return new BTServiceFileUpload({
            ext: file.extension,
            fileName: file.title,
            hasChanged: true,
            imagePath: file.docPath,
            tempId: file.id,
            url: file.docPath,
        });
    }

    return new BTServiceFileUpload({
        ext: file.ext,
        fileName: file.title,
        hasChanged: hasChanged,
        imagePath: file.imagePath,
        tempId: file.id,
        url: file.url,
    });
}

export const CustomFieldsFormValuesValidator = yup
    .array()
    .label("Custom Fields")
    .notOneOf([undefined, null])
    .of(
        yup.lazy<ICustomFieldFormValues>((value) => {
            return yup.object<ICustomFieldFormValues>().shape({
                id: yup.number().required().label("id"),
                value: getValueValidator(value),
            });
        }),
    );

export const CustomFieldsFormReadOnlyValuesValidator = yup
    .array()
    .label("Custom Fields")
    .notOneOf([undefined, null])
    .of(
        yup.lazy<ICustomFieldFormValues>((value) => {
            return yup.object<ICustomFieldFormValues>().shape({
                id: yup.number().required().label("id"),
                value: getValueValidator(value).notRequired(),
            });
        }),
    );

export const PropsToValueMapping = (props: IHasCustomFieldContainer): ICustomFieldFormValues[] => {
    const dropdownTypes = [
        CustomFieldDataType.DropDownList,
        CustomFieldDataType.ListOfSubs,
        CustomFieldDataType.ListOfUsers,
        CustomFieldDataType.DropDownCheckList,
        CustomFieldDataType.MultiSelectSubs,
        CustomFieldDataType.MultiSelectUsers,
    ];
    return props.customFields.map((cf: ICustomFieldResponse) => {
        const result = { ...cf } as ICustomFieldFormValues;
        if (cf.typeId === CustomFieldDataType.File) {
            // custom field file types send down a "selectedFile" response but expect a "value" request
            const selectedFile = cf.selectedFile;
            if (selectedFile !== undefined) {
                // map BTFile response to the attach file request
                const attachFileRequest = mapToBTServiceFileUpload(selectedFile, false);

                result.value = attachFileRequest;
            }
        } else if (cf.typeId === CustomFieldDataType.Date) {
            const value = cf.value !== "" && cf.value !== null ? moment(cf.value as string) : null;
            result.value = value;
        } else if (dropdownTypes.includes(cf.typeId)) {
            const cfValue = props.customFieldOptions[cf.optionsKey];
            result.dropdownItems = cfValue?.map(
                (c: IDropDownItem) =>
                    new BTSelectItem<any>({ id: c.id, name: c.name, extraData: c.extraData }),
            );
        }
        return result;
    });
};
